import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import alienXImage from '../assets/images/alien_x_8k_1785416801849.jpg';

interface OmnitrixButtonProps {
  onActivate: () => void;
}

export default function OmnitrixButton({ onActivate }: OmnitrixButtonProps) {
  const [phase, setPhase] = useState<"idle" | "alienX">("idle");

  // Preload image to prevent lag
  useEffect(() => {
    const img = new Image();
    img.src = alienXImage;
  }, []);

  const handleClick = () => {
    if (phase !== "idle") return;
    setPhase("alienX");

    // Text to speech "Welcome to Alien X Factory" in deep male voice
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance("Welcome to Alien X Factory");
      
      const setVoiceAndSpeak = () => {
        const voices = window.speechSynthesis.getVoices();
        let targetVoice = voices.find(v => 
          v.name.toLowerCase().includes("male") || 
          v.name.toLowerCase().includes("david") || 
          v.name.toLowerCase().includes("mark") || 
          v.name.toLowerCase().includes("guy") ||
          v.name.toLowerCase().includes("google uk english male")
        );
        if (!targetVoice) {
          targetVoice = voices.find(v => 
            !v.name.toLowerCase().includes("female") && 
            !v.name.toLowerCase().includes("zira") &&
            !v.name.toLowerCase().includes("samantha") &&
            !v.name.toLowerCase().includes("victoria")
          );
        }
        if (targetVoice) {
          utterance.voice = targetVoice;
        }
        utterance.pitch = 0.1; // Very deep pitch
        utterance.rate = 0.8;  // Slower rate for dramatic effect
        utterance.volume = 1;
        window.speechSynthesis.speak(utterance);
      };

      if (window.speechSynthesis.getVoices().length > 0) {
        setVoiceAndSpeak();
      } else {
        window.speechSynthesis.onvoiceschanged = setVoiceAndSpeak;
      }
    }

    // Transition smoothly to the main portal after Alien X display
    setTimeout(() => {
      onActivate();
    }, 3500);
  };

  return (
    <AnimatePresence mode="wait">
      {phase === "idle" && (
        <motion.div
          key="idle"
          className="fixed inset-0 z-50 flex flex-col items-center justify-center bg-black/90 backdrop-blur-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, scale: 1.2, filter: "blur(10px)" }}
          transition={{ duration: 0.5 }}
        >
          <div className="text-center space-y-12">
            <motion.h1 
              className="text-4xl sm:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-600 tracking-[0.3em] uppercase font-sans drop-shadow-[0_0_15px_rgba(34,197,94,0.5)]"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 3, repeat: Infinity }}
            >
              Omnitrix Protocol
            </motion.h1>
            
            <motion.button
              onClick={handleClick}
              className="relative group cursor-pointer mx-auto"
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9, rotateX: 20, rotateY: 20 }}
              style={{ perspective: 1000 }}
            >
              {/* Realistic Omnitrix Dial */}
              <motion.div 
                className="w-56 h-56 rounded-full border-[12px] border-zinc-700 bg-black shadow-[0_0_60px_rgba(34,197,94,0.6)] flex items-center justify-center relative overflow-hidden group-hover:border-zinc-500 transition-colors duration-500"
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
              >
                {/* Inner Ring Glow */}
                <div className="absolute inset-0 rounded-full border-4 border-green-500/50 shadow-[inset_0_0_30px_rgba(34,197,94,0.8)]" />
                
                {/* Omnitrix Hourglass Logo */}
                <svg viewBox="0 0 100 100" className="w-[120%] h-[120%] drop-shadow-[0_0_20px_#22c55e]">
                  <polygon points="0,0 100,0 50,50" fill="#22c55e" />
                  <polygon points="0,100 100,100 50,50" fill="#22c55e" />
                </svg>

                {/* Dial details */}
                <div className="absolute top-0 w-3 h-6 bg-zinc-800 rounded-b-md" />
                <div className="absolute bottom-0 w-3 h-6 bg-zinc-800 rounded-t-md" />
                <div className="absolute left-0 w-6 h-3 bg-zinc-800 rounded-r-md" />
                <div className="absolute right-0 w-6 h-3 bg-zinc-800 rounded-l-md" />
              </motion.div>
            </motion.button>
            
            <div className="mt-8 flex flex-col items-center gap-5 px-4">
              <div className="text-green-400 font-mono tracking-widest text-lg uppercase animate-pulse drop-shadow-[0_0_8px_#22c55e]">
                Click to Transform
              </div>
              
              <div className="bg-rose-950/30 border border-rose-500/40 p-4 rounded-xl max-w-sm mx-auto shadow-[0_0_15px_rgba(225,29,72,0.2)]">
                <h3 className="text-rose-500 font-bold mb-2 flex items-center justify-center gap-2">
                  <span className="text-lg">⚠️</span> 18+ AGE RESTRICTION
                </h3>
                <p className="text-zinc-300 text-xs sm:text-sm leading-relaxed">
                  This portal contains adult material. By clicking the Omnitrix above, you confirm that you are 18 years of age or older.
                </p>
              </div>

              <button 
                onClick={() => window.location.href = "https://www.google.com"}
                className="px-6 py-2.5 rounded-full border border-zinc-700 text-zinc-400 hover:bg-zinc-800 hover:text-white transition-all text-xs font-mono uppercase tracking-widest"
              >
                Cancel / Leave Now
              </button>
            </div>
          </div>
        </motion.div>
      )}

      {phase === "alienX" && (
        <motion.div
          key="alienX"
          className="fixed inset-0 z-[100] bg-black flex items-center justify-center overflow-hidden"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8 }}
        >
          {/* Green flash effect */}
          <motion.div
            className="absolute inset-0 bg-green-500 z-30 pointer-events-none"
            initial={{ opacity: 1 }}
            animate={{ opacity: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          />

          {/* Alien X 8K Image Container */}
          <motion.div
            className="relative w-full h-full flex items-center justify-center bg-black"
            initial={{ scale: 1.1, filter: "brightness(0.3) contrast(1.5)" }}
            animate={{ scale: 1, filter: "brightness(1) contrast(1.1)" }}
            transition={{ duration: 3, ease: "easeOut" }}
          >
            {/* The high quality 8K Alien X Image */}
            <div 
              className="absolute inset-0 bg-center bg-contain bg-no-repeat sm:bg-cover opacity-95"
              style={{ 
                backgroundImage: `url(${alienXImage})` 
              }}
            />
            
            {/* Cosmic glow radial vignette */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_30%,black_90%)] pointer-events-none" />

            {/* Deep male voice text overlay */}
            <motion.div 
              className="absolute inset-0 flex flex-col items-center justify-end pb-16 sm:pb-24 z-20 pointer-events-none px-4 text-center"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 1.2 }}
            >
               <motion.h2 
                 className="text-3xl sm:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-b from-green-200 via-green-400 to-emerald-700 tracking-[0.3em] uppercase font-sans drop-shadow-[0_0_25px_rgba(34,197,94,0.8)]"
                 animate={{ scale: [0.98, 1.02, 0.98] }}
                 transition={{ duration: 3, repeat: Infinity }}
               >
                 Welcome to Alien X Factory
               </motion.h2>
               <div className="mt-3 text-xs sm:text-sm font-mono text-green-300 tracking-[0.5em] uppercase opacity-80">
                 Universal Reality Engine Initialized
               </div>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

